import { useState, useEffect, Dispatch } from 'react';
import { PolicyInfoResponse } from 'all-in-one-shared-api/dist/apis/policy/PolicyInfoResponse';

import { SelectionName, SelectionPeriod } from 'pages/benefits/types/benefitTypes';

export const getAllMemberNamesByPolicyInfo = (policies: PolicyInfoResponse[]): SelectionName[] | undefined => {
  if (!policies) return;

  const selectionNames: SelectionName[] = [];
  policies.forEach((policy) => {
    if (selectionNames.some((name) => name.memberType === policy.memberType)) return;
    selectionNames.push({
      id: `${policy.memberId}-${policy.memberType}`,
      label: `${policy.firstName} ${policy.lastName}`,
      memberId: policy.memberId ?? '',
      memberType: policy.memberType ?? '',
      firstName: policy.firstName ?? '',
      lastName: policy.lastName ?? '',
    });
  });

  return selectionNames;
};

export const getAllPeriodsByMember = (
  policies: PolicyInfoResponse[],
  memberName: Partial<SelectionName>,
): SelectionPeriod[] | undefined => {
  if (!policies || !memberName) return;
  const memberPolicies = policies.filter(
    (policy) => policy.memberId === memberName.memberId && policy.memberType === memberName.memberType,
  );
  if (!memberPolicies) return;
  return memberPolicies
    .map((policy) => {
      return {
        id: `${policy.memberId}-${policy.memberType}-${policy.memberStartDate}`,
        startDate: policy.memberStartDate ?? '',
        endDate: policy.memberEndDate ?? '',
      };
    })
    .sort((first, second) => Date.parse(second.startDate) - Date.parse(first.startDate));
};

interface useGetBenefitFiltersProp {
  policyInfos: PolicyInfoResponse[];
  selectedFilterName: Partial<SelectionName>;
  selectedFilterPeriod: Partial<SelectionPeriod>;
  setSelectedFilterName: Dispatch<React.SetStateAction<Partial<SelectionName>>>;
  setSelectedFilterPeriod: Dispatch<React.SetStateAction<Partial<SelectionPeriod>>>;
}

function useGetBenefitFilters({
  policyInfos,
  selectedFilterName,
  selectedFilterPeriod,
  setSelectedFilterName,
  setSelectedFilterPeriod,
}: useGetBenefitFiltersProp) {
  const [selectedFilterNameOptions, setSelectedFilterNameOptions] = useState<SelectionName[]>([]);
  const [selectedFilterPeriodOptions, setSelectedFilterPeriodOptions] = useState<SelectionPeriod[]>([]);

  const onFilterNameSelected = (key: string): void => {
    const filterName = selectedFilterNameOptions.find((opt) => opt.id === key);
    if (filterName) setSelectedFilterName(filterName);
  };
  const onFilterPeriodSelected = (key: string): void => {
    const filterPeriod = selectedFilterPeriodOptions.find((opt) => opt.id === key);
    if (filterPeriod) setSelectedFilterPeriod(filterPeriod);
  };

  useEffect(() => {
    if (policyInfos) {
      const filterNameOptions = getAllMemberNamesByPolicyInfo(policyInfos);
      if (filterNameOptions && filterNameOptions.length > 0) {
        setSelectedFilterNameOptions(filterNameOptions);
        setSelectedFilterName(filterNameOptions[0]);
      }
    }
  }, [policyInfos, setSelectedFilterName]);

  useEffect(() => {
    if (policyInfos && selectedFilterName) {
      const periods = getAllPeriodsByMember(policyInfos, selectedFilterName);
      if (periods && periods.length > 0) {
        setSelectedFilterPeriodOptions(periods);
        setSelectedFilterPeriod(periods[0]);
      }
    }
  }, [policyInfos, selectedFilterName, setSelectedFilterPeriod]);

  return {
    selectedFilterNameOptions,
    selectedFilterName,
    onFilterNameSelected,
    selectedFilterPeriodOptions,
    selectedFilterPeriod,
    onFilterPeriodSelected,
  };
}

export default useGetBenefitFilters;
